import { useEffect, useRef, useState } from "react";

const ALWAYS_HOT = new Set(["ClockIn"]);

/**
 * Mount card content only when near the viewport. Preserves min-height so
 * react-grid-layout does not jump when content appears.
 */
export default function ViewportLazyContent({
  cardName,
  children,
  rootMargin = "200px",
  placeholderMinHeight = 120,
}) {
  const alwaysHot = ALWAYS_HOT.has(cardName) || cardName?.alwaysHot;
  const ref = useRef(null);
  const [visible, setVisible] = useState(!!alwaysHot);

  useEffect(() => {
    if (alwaysHot || visible) return undefined;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [alwaysHot, visible, rootMargin]);

  if (alwaysHot) {
    return children;
  }

  return (
    <div
      ref={ref}
      style={{
        minHeight: placeholderMinHeight,
        width: "100%",
        height: "100%",
      }}
    >
      {visible ? children : null}
    </div>
  );
}
